"use client";
import Link from 'next/link';
import React from 'react';

/**
 * Shared navigation link, used by the desktop pill rail, the "More"
 * overflow menu, and the mobile drawer so all three stay visually
 * consistent.
 *
 * Premium pass (2026-08-04): the ad-hoc inline styles were replaced with
 * the `.pr-nav-pill` / `.pr-menu-item` primitives in styles/globals.css.
 * Icons now sit in their own tinted "well" that lights up on hover and
 * active, the active pill carries a gradient fill plus a small underline
 * marker (so the current section isn't communicated by color alone), and
 * menu rows can carry an optional one-line `hint` describing the
 * destination.
 */
export default function NavLinkItem({
  href,
  label,
  Icon,
  onClick,
  active,
  mobile,
  hint,
  className = '',
}: {
  href: string;
  label: string;
  Icon?: React.FC;
  onClick?: () => void;
  active?: boolean;
  /** Render as a full-width menu row (drawer / dropdown) instead of a pill. */
  mobile?: boolean;
  /** Optional supporting line, menu rows only. */
  hint?: string;
  className?: string;
}) {
  if (mobile) {
    return (
      <Link
        href={href}
        onClick={onClick}
        data-active={!!active}
        aria-current={active ? 'page' : undefined}
        className={`pr-menu-item ${className}`.trim()}
      >
        {Icon ? (
          <span className="pr-menu-icon" aria-hidden="true">
            <Icon />
          </span>
        ) : null}
        <span className="min-w-0 flex-1">
          <span className="pr-menu-label block truncate">{label}</span>
          {hint ? <span className="pr-menu-hint truncate">{hint}</span> : null}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="none"
          className="h-3.5 w-3.5 shrink-0 opacity-45"
          aria-hidden="true"
        >
          <path
            d="M7.5 4.5L13 10l-5.5 5.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Link>
    );
  }

  return (
    <Link
      href={href}
      onClick={onClick}
      data-active={!!active}
      aria-current={active ? 'page' : undefined}
      className={`pr-nav-pill ${className}`.trim()}
    >
      {Icon ? (
        <span className="pr-nav-icon" aria-hidden="true">
          <Icon />
        </span>
      ) : null}
      <span>{label}</span>
    </Link>
  );
}
